/**
 * Motion design tokens for funontherun.co
 *
 * Single source of truth for every animation in the app. Every component
 * that animates MUST source its duration + easing from this file so the
 * motion language stays consistent.
 *
 * Movement vocabulary constraints:
 *   - Y-shift: 12-32px
 *   - Scale: 0.96 → 1, or 1 → 1.02
 *   - Opacity: 0 → 1
 *   - Blur reveal: 8px → 0 (sparing use)
 *   - X-slide: 16-40px
 *   - No rotation > 6deg
 *   - No bouncy/elastic except the push-alert chip arrival
 */

// ---------- Timing tokens (in seconds for Framer Motion) ----------
export const DURATION = {
  micro:    0.15,   // 120-180ms — focus rings, tiny indicators
  ui:       0.20,   // 180-220ms — button hover, link underline
  card:     0.38,   // 300-450ms — card reveal
  section:  0.60,   // 500-700ms — section reveal
  scroll:   0.75,   // 600-900ms — scroll-narrative transitions
};

// ---------- Easing presets ----------
export const EASE = {
  snappyUI:      [0.2, 0.8, 0.2, 1],   // UI feedback — slight overshoot before settle
  softEntrance:  [0.22, 1, 0.36, 1],   // Content reveals — slow start, fast finish
  premiumHover:  [0.0, 0.0, 0.2, 1],   // ease-out style — buttery hover
};

// ---------- Mobile reducer ----------
// Mobile = scale durations × 0.85 and reduce stagger by 35%.
export function mobileScale(value, isMobile) {
  return isMobile ? value * 0.85 : value;
}
export function mobileStagger(staggerSec, isMobile) {
  return isMobile ? staggerSec * 0.65 : staggerSec;
}

// ---------- Reusable variants ----------

// Generic fade + Y-up reveal (use most often). Honors `delay`.
export const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (custom = {}) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: custom.duration ?? DURATION.card,
      delay:    custom.delay ?? 0,
      ease:     EASE.softEntrance,
    },
  }),
};

export const slideInLeft = {
  hidden: { opacity: 0, x: -24 },
  show: (custom = {}) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: custom.duration ?? DURATION.card,
      delay:    custom.delay ?? 0,
      ease:     EASE.softEntrance,
    },
  }),
};

export const slideInRight = {
  hidden: { opacity: 0, x: 24 },
  show: (custom = {}) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: custom.duration ?? DURATION.card,
      delay:    custom.delay ?? 0,
      ease:     EASE.softEntrance,
    },
  }),
};

// Pre-blurred entrance for headline impact moments. Use sparingly.
export const blurReveal = {
  hidden: { opacity: 0, y: 16, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: DURATION.section, ease: EASE.softEntrance },
  },
};

// Stagger parent — children fire one after another with a small offset.
export const staggerParent = (childStagger = 0.08, delayChildren = 0) => ({
  hidden: {},
  show: {
    transition: {
      staggerChildren: childStagger,
      delayChildren,
    },
  },
});

// Notification drop — used for the push-alert hero centerpiece.
// One single-cycle micro-vibration on arrival, then settle.
export const notificationDrop = {
  hidden: { opacity: 0, x: 240, y: -16 },
  show: {
    opacity: 1,
    x: [240, 0, -3, 2, 0],    // arrive, then micro-vibrate
    y: [-16, 0, 0, 0, 0],
    transition: {
      duration: 0.42,
      times: [0, 0.78, 0.88, 0.95, 1],
      ease: EASE.snappyUI,
    },
  },
};

// Counter pulse — used in conjunction with the notification drop.
export const counterPulse = {
  scale: [1, 1.06, 1],
  transition: { duration: 0.26, delay: 0.42, ease: EASE.snappyUI },
};

// Reduced-motion variant — collapses to opacity-only.
export const reducedMotion = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
};
